(() => {
  'use strict';

  const CART_MUTATION = /\/cart\/(?:add|change|update)(?:\.js)?$/i;
  const originalFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;
  const pendingButtons = new Set();
  let refreshTimer = 0;
  let refreshPromise = null;
  let refreshQueued = false;

  const urlOf = (input) =>
    typeof input === 'string' ? input : input && typeof input.url === 'string' ? input.url : '';

  const isCartMutation = (input) => {
    const value = urlOf(input);
    if (!value) return false;
    try {
      return CART_MUTATION.test(new URL(value, location.href).pathname);
    } catch (error) {
      return CART_MUTATION.test(value.split('?')[0]);
    }
  };

  const cartUrl = () => {
    const root =
      window.Shopify && Shopify.routes && typeof Shopify.routes.root === 'string'
        ? Shopify.routes.root
        : '/';
    return new URL(`${root.replace(/\/?$/, '/')}cart.js`, location.origin).toString();
  };

  const renderCount = (value) => {
    const count = Math.max(0, parseInt(value, 10) || 0);
    const label = `${count} item${count === 1 ? '' : 's'} in cart`;

    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = String(count);
    });

    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      const visible =
        bubble.querySelector('[data-cart-count]') ||
        bubble.querySelector('span[aria-hidden="true"]') ||
        bubble.querySelector('span:not(.visually-hidden)');
      if (visible) visible.textContent = String(count);

      const hidden = bubble.querySelector('.visually-hidden');
      if (hidden) hidden.textContent = label;

      bubble.classList.toggle('hidden', count === 0);
      bubble.setAttribute('aria-label', label);
      bubble.setAttribute('aria-live', 'polite');
    });

    document
      .querySelectorAll('#cart-icon-bubble .cart-count-bubble span:first-child')
      .forEach((node) => {
        node.textContent = String(count);
      });

    return count;
  };

  const settleButtons = () => {
    pendingButtons.forEach((button) => {
      button.classList.remove('loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    });
    pendingButtons.clear();
  };

  const refreshCount = () => {
    if (!originalFetch) return Promise.resolve(null);

    if (refreshPromise) {
      refreshQueued = true;
      return refreshPromise;
    }

    refreshPromise = originalFetch(cartUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart refresh failed');
        return response.json();
      })
      .then((cart) => {
        renderCount(cart.item_count);
        settleButtons();
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
        if (refreshQueued) {
          refreshQueued = false;
          scheduleRefresh(80);
        }
      });

    return refreshPromise;
  };

  const scheduleRefresh = (delay = 70) => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCount, delay);
  };

  const watchFetch = () => {
    if (!originalFetch || (window.fetch && window.fetch.__ashokaCartWatch)) return;

    const watchedFetch = (...args) =>
      originalFetch(...args).then((response) => {
        if (response.ok && isCartMutation(args[0])) scheduleRefresh();
        return response;
      });

    Object.defineProperty(watchedFetch, '__ashokaCartWatch', { value: true });
    window.fetch = watchedFetch;
  };

  const watchXhr = () => {
    const proto = window.XMLHttpRequest && XMLHttpRequest.prototype;
    if (!proto || proto.__ashokaCartWatch) return;

    const open = proto.open;
    const send = proto.send;

    proto.open = function (method, url) {
      this.__ashokaCartMutation = isCartMutation(url);
      return open.apply(this, arguments);
    };

    proto.send = function () {
      if (this.__ashokaCartMutation) {
        this.addEventListener(
          'loadend',
          function () {
            if (this.status >= 200 && this.status < 400) scheduleRefresh();
          },
          { once: true }
        );
      }
      return send.apply(this, arguments);
    };

    Object.defineProperty(proto, '__ashokaCartWatch', { value: true });
  };

  const addButtonFeedback = () => {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const action = form.getAttribute('action') || form.action || '';
        if (!/\/cart\/add(?:\.js)?(?:\?|$)/i.test(action)) return;

        const button =
          form.querySelector('[data-btn-addToCart]') ||
          form.querySelector('[name="add"]') ||
          form.querySelector('.product-form__submit');

        if (!button || button.disabled) return;

        button.classList.add('loading');
        button.setAttribute('aria-busy', 'true');
        pendingButtons.add(button);

        setTimeout(() => {
          if (!pendingButtons.has(button)) return;
          pendingButtons.delete(button);
          button.classList.remove('loading');
          button.removeAttribute('aria-busy');
          button.removeAttribute('aria-disabled');
        }, 8000);
      },
      true
    );
  };

  const installMotionCss = () => {
    if (document.getElementById('ashoka-no-cosmetic-motion')) return;

    const style = document.createElement('style');
    style.id = 'ashoka-no-cosmetic-motion';
    style.textContent = `
      html.ashoka-no-motion{scroll-behavior:auto!important}
      html.ashoka-no-motion .scroll-trigger,
      html.ashoka-no-motion .animate--ambient,
      html.ashoka-no-motion .an-tilt-shaking,
      html.ashoka-no-motion .an-vertical-shaking,
      html.ashoka-no-motion .an-horizontal-shaking{
        animation:none!important;transition:none!important;transform:none!important;opacity:1!important
      }
      html.ashoka-no-motion .animated-loading,
      html.ashoka-no-motion .media--loading-effect{
        animation:none!important;background:#f6f6f6!important
      }
      html.ashoka-no-motion .animated-loading>img,
      html.ashoka-no-motion .animated-loading>span,
      html.ashoka-no-motion .media--loading-effect>img,
      html.ashoka-no-motion .media--loading-effect>picture{opacity:1!important}
      html.ashoka-no-motion .media-loading,
      html.ashoka-no-motion .background-cursor-wrapper{display:none!important}
      html.ashoka-no-motion .marquee.active-screen .marquee__list,
      html.ashoka-no-motion .marquee__list,
      html.ashoka-no-motion .marquee-ltr,
      html.ashoka-no-motion .marquee-rtl{
        animation:none!important;transform:none!important;will-change:auto!important
      }
      html.ashoka-no-motion .ashoka-static-trust{display:block!important;overflow:hidden!important}
      html.ashoka-no-motion .ashoka-static-trust .marquee__list{
        display:flex!important;width:100%!important;align-items:center!important;
        justify-content:center!important;flex-wrap:wrap!important;white-space:normal!important;
        gap:6px 18px!important;padding:0 12px!important
      }
      html.ashoka-no-motion .ashoka-static-trust .marquee__item{
        flex:0 1 auto!important;padding:3px 8px!important;text-align:center!important;transition:none!important
      }
      @media(max-width:767px){
        html.ashoka-no-motion .ashoka-static-trust .marquee__list{gap:2px 8px!important;padding:0 8px!important}
        html.ashoka-no-motion .ashoka-static-trust .marquee__item{
          font-size:12px!important;line-height:16px!important;padding:2px 5px!important
        }
        html.ashoka-no-motion .ashoka-static-trust .marquee__item:nth-child(n+4){display:none!important}
      }
    `;
    document.head.appendChild(style);
  };

  const stopCosmeticMotion = (root = document) => {
    document.documentElement.classList.add('ashoka-no-motion');
    if (document.body) document.body.classList.remove('show_image_loading', 'show_effect_close');

    root
      .querySelectorAll(
        '.scroll-trigger.animate--slide-in,.scroll-trigger.animate--fade-in,.animate--ambient'
      )
      .forEach((node) => {
        node.classList.remove(
          'scroll-trigger',
          'animate--slide-in',
          'animate--fade-in',
          'animate--ambient'
        );
      });

    root.querySelectorAll('.template-index .marquee').forEach((marquee) => {
      marquee.classList.add('ashoka-static-trust');
      marquee.classList.remove('active-screen');

      const lists = Array.from(marquee.children).filter((node) =>
        node.classList.contains('marquee__list')
      );
      lists.slice(1).forEach((node) => node.remove());
      if (lists[0]) lists[0].classList.remove('scroll-trigger', 'animate--fade-in');
    });
  };

  const clearDiscountState = () => {
    try {
      ['discount_code', 'cartDiscount'].forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
    } catch (error) {}
  };

  const init = () => {
    installMotionCss();
    stopCosmeticMotion();
    clearDiscountState();
    watchFetch();
    watchXhr();
    addButtonFeedback();

    ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxProduct:added'].forEach((name) => {
      document.addEventListener(name, () => scheduleRefresh(30));
    });

    document.addEventListener('shopify:section:load', (event) => {
      stopCosmeticMotion(event.target || document);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
